import { useState } from 'react';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import { uiConfirm } from '../utils/dialogs.js';

/**
 * Consignments — pallets grouped to go out together, linked to whatever
 * order is behind them (a sales order, a fulfilment order, or just a
 * reference), with one manifest for all the pallets. Used as a tab on
 * Storage Units, and from the pallet list ("Add to consignment").
 */

function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const pad = size === 'sm' ? '6px 12px' : '9px 18px';
  const styles = {
    primary: { background: 'var(--pld-green)', color: '#fff', border: 'none' },
    ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' },
  };
  return <button {...props} style={{ padding: pad, fontSize: size === 'sm' ? 13 : 14, fontWeight: 600, borderRadius: 8, cursor: props.disabled ? 'not-allowed' : 'pointer', opacity: props.disabled ? 0.6 : 1, whiteSpace: 'nowrap', ...styles[variant], ...props.style }}>{children}</button>;
}
function Modal({ title, onClose, width = 560, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
        </div>
        <div style={{ padding: '18px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) { return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>; }
const inputStyle = { padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', fontFamily: 'inherit' };
function Input(props) { return <input {...props} style={{ ...inputStyle, ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={inputStyle}>{children}</select>; }
function Textarea(props) { return <textarea {...props} style={{ ...inputStyle, resize: 'vertical', ...props.style }} />; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function Err({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{msg}</p> : null; }
const fmt = d => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
// useApi hands back the response's `data` already.
const arr = d => (Array.isArray(d) ? d : (d?.data ?? []));

const STATUS_STYLE = { open: { bg: '#fef3c7', color: '#92400e', label: 'Open' }, dispatched: { bg: '#dbeafe', color: '#1e40af', label: 'Dispatched' }, cancelled: { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' } };
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  return <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>;
}

/** Open a PDF from the API in a new tab. */
export async function openPdf(path) {
  const win = window.open('', '_blank');
  try {
    const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Couldn't open it (HTTP ${res.status})`); }
    const url = URL.createObjectURL(await res.blob());
    if (win) win.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { if (win) win.close(); alert(e.message); }
}

const ORDER_KINDS = [['sales_order', 'A sales order'], ['fulfilment_order', 'A fulfilment order'], ['other', 'Something else (transfer, client stock, collection…)']];

export function ConsignmentForm({ consignment, palletIds = [], presetOrder, onClose, onSaved }) {
  const c = consignment || {};
  const [form, setForm] = useState({
    title: c.title || '', order_kind: c.order_kind || presetOrder?.kind || 'other',
    sales_order_id: c.sales_order_id || (presetOrder?.kind === 'sales_order' ? presetOrder.id : ''),
    fulfilment_order_id: c.fulfilment_order_id || (presetOrder?.kind === 'fulfilment_order' ? presetOrder.id : ''),
    order_reference: c.order_reference || '', client_id: c.client_id || '',
    consignee_name: c.consignee_name || '', consignee_address: c.consignee_address || '', consignee_contact: c.consignee_contact || '',
    haulier: c.haulier || '', haulier_ref: c.haulier_ref || '', collection_date: c.collection_date ? String(c.collection_date).slice(0, 10) : '', notes: c.notes || '',
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const { data: so } = useApi('/orders/sales?limit=200');
  const { data: fo } = useApi('/orders/fulfilment?limit=200');
  const { data: cl } = useApi('/clients');
  const sales = arr(so);
  const fulfil = arr(fo);
  const clients = cl?.clients ?? cl?.data ?? (Array.isArray(cl) ? cl : []);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      const r = c.id
        ? await apiFetch(`/consignments/${c.id}`, { method: 'PUT', body: form })
        : await apiFetch('/consignments', { method: 'POST', body: { ...form, pallet_ids: palletIds } });
      onSaved(r?.data);
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title={c.id ? `Edit ${c.consignment_ref}` : `New consignment${palletIds.length ? ` — ${palletIds.length} pallet${palletIds.length === 1 ? '' : 's'}` : ''}`} onClose={onClose} width={640}>
      <Err msg={err} />
      <Field label="What's it for?">
        <Select value={form.order_kind} onChange={set('order_kind')}>{ORDER_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
      </Field>
      {form.order_kind === 'sales_order' && (
        <Field label="Sales order">
          <Select value={form.sales_order_id} onChange={set('sales_order_id')}>
            <option value="">Choose…</option>
            {sales.map(o => <option key={o.id} value={o.id}>{o.order_ref} — {o.outlet_name || o.delivery_city || o.customer_type} ({o.status})</option>)}
          </Select>
        </Field>
      )}
      {form.order_kind === 'fulfilment_order' && (
        <Field label="Fulfilment order">
          <Select value={form.fulfilment_order_id} onChange={set('fulfilment_order_id')}>
            <option value="">Choose…</option>
            {fulfil.map(o => <option key={o.id} value={o.id}>{o.order_ref} — {o.recipient_name} ({o.status})</option>)}
          </Select>
        </Field>
      )}
      <Grid2>
        <Field label={form.order_kind === 'other' ? 'Reference (their PO, or what it is)' : 'Their reference (optional)'}><Input value={form.order_reference} onChange={set('order_reference')} placeholder="e.g. Transfer to client's bond — PO 4471" /></Field>
        <Field label="Client (whose stock)">
          <Select value={form.client_id} onChange={set('client_id')}><option value="">— PLD / none —</option>{clients.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>
        </Field>
      </Grid2>
      <Grid2>
        <Field label="Going to"><Input value={form.consignee_name} onChange={set('consignee_name')} placeholder={form.order_kind === 'other' ? 'Consignee' : 'Leave blank to take it from the order'} /></Field>
        <Field label="Contact"><Input value={form.consignee_contact} onChange={set('consignee_contact')} placeholder="name / phone" /></Field>
      </Grid2>
      <Field label="Delivery address"><Textarea rows={2} value={form.consignee_address} onChange={set('consignee_address')} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Haulier"><Input value={form.haulier} onChange={set('haulier')} /></Field>
        <Field label="Booking ref"><Input value={form.haulier_ref} onChange={set('haulier_ref')} /></Field>
        <Field label="Collection date"><Input type="date" value={form.collection_date} onChange={set('collection_date')} /></Field>
      </div>
      <Field label="Name (optional)"><Input value={form.title} onChange={set('title')} placeholder="e.g. Cimbroni October stock move" /></Field>
      <Field label="Notes (printed on the manifest)"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={busy}>{busy ? 'Saving…' : c.id ? 'Save' : 'Create consignment'}</Btn>
      </div>
    </Modal>
  );
}

/** Put pallets on an open consignment, or start a new one with them. */
export function AddToConsignmentModal({ palletIds, onClose, onDone }) {
  const { data } = useApi('/consignments?status=open');
  const open = arr(data);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  async function addTo(c) {
    setErr('');
    try { await apiFetch(`/consignments/${c.id}/pallets`, { method: 'POST', body: { pallet_ids: palletIds } }); onDone(c.id); }
    catch (e) { setErr(e.message); }
  }
  if (creating) return <ConsignmentForm palletIds={palletIds} onClose={onClose} onSaved={d => onDone(d?.id)} />;
  return (
    <Modal title={`Add ${palletIds.length} pallet${palletIds.length === 1 ? '' : 's'} to a consignment`} onClose={onClose} width={520}>
      <Err msg={err} />
      <Btn onClick={() => setCreating(true)} style={{ width: '100%', marginBottom: 12 }}>+ New consignment</Btn>
      {open.length > 0 && <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '4px 0 6px' }}>Or add to an open one</div>}
      {open.map(c => (
        <div key={c.id} onClick={() => addTo(c)} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '9px 12px', marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
          <strong style={{ fontFamily: 'monospace' }}>{c.consignment_ref}</strong> · {c.order_label || c.title || 'no order'} · {c.consignee_name || 'consignee not set'}
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.pallet_count} pallet{c.pallet_count === 1 ? '' : 's'}{c.collection_date ? ` · collection ${fmt(c.collection_date)}` : ''}</div>
        </div>
      ))}
    </Modal>
  );
}

function AddPalletsModal({ consignment, onClose, onDone }) {
  const { data } = useApi('/storage-units');
  const { data: openData } = useApi('/consignments?status=open');
  const taken = new Set(arr(openData).flatMap(c => c.pallet_refs || []));
  const here = new Set(consignment.pallets.map(p => p.id));
  const units = (data?.storage_units ?? []).filter(u => !here.has(u.id));
  const [pick, setPick] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const shown = units.filter(u => !q.trim() || [u.unit_ref, u.name, u.location_code, u.owner_client_name].some(v => String(v || '').toLowerCase().includes(q.trim().toLowerCase())));
  async function add() {
    setErr('');
    try { await apiFetch(`/consignments/${consignment.id}/pallets`, { method: 'POST', body: { pallet_ids: [...pick] } }); onDone(); }
    catch (e) { setErr(e.message); }
  }
  return (
    <Modal title={`Add pallets to ${consignment.consignment_ref}`} onClose={onClose} width={640}>
      <Err msg={err} />
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search ref, name, location, owner…" style={{ marginBottom: 10 }} />
      <div style={{ maxHeight: 380, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
        {shown.map(u => {
          const busy = taken.has(u.unit_ref);
          return (
            <label key={u.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: 13, opacity: busy ? 0.5 : 1 }}>
              <input type="checkbox" disabled={busy} checked={pick.has(u.id)} onChange={e => setPick(s => { const n = new Set(s); e.target.checked ? n.add(u.id) : n.delete(u.id); return n; })} />
              <strong style={{ fontFamily: 'monospace' }}>{u.unit_ref}</strong>
              <span style={{ flex: 1 }}>{u.name || ''}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{u.location_code || 'no location'}{busy ? ' · on another consignment' : ''}</span>
            </label>
          );
        })}
        {!shown.length && <p style={{ padding: 12, margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>No pallets to add.</p>}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={add} disabled={!pick.size}>Add {pick.size || ''} pallet{pick.size === 1 ? '' : 's'}</Btn>
      </div>
    </Modal>
  );
}

export function ConsignmentDetail({ id, onBack, onOpenPallet }) {
  const { data, loading, refetch } = useApi(`/consignments/${id}`, [id]);
  const c = data?.consignment_ref ? data : data?.data;
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState('');
  async function act(path, confirmText) {
    if (confirmText && !(await uiConfirm(confirmText))) return;
    setErr('');
    try { await apiFetch(`/consignments/${id}/${path}`, { method: 'POST', body: {} }); refetch(); } catch (e) { setErr(e.message); }
  }
  // Dispatching takes the stock off: through the order behind it, or off the
  // pallets themselves — asking how any in-bond spirit is leaving.
  async function dispatch(bondedAs) {
    if (c.needs_bonded_as && !bondedAs) { setModal('bond'); return; }
    if (!bondedAs) {
      const text = c.order_kind === 'other' || !c.order_label
        ? `Mark ${c.consignment_ref} as dispatched? The stock on its pallets comes off Blake, and the manifest is fixed as it is now.`
        : `Mark ${c.consignment_ref} as dispatched? This despatches ${c.order_label} too (if it isn't already), which takes its stock off Blake. The manifest is fixed as it is now.`;
      if (!(await uiConfirm(text))) return;
    }
    setErr(''); setModal(null);
    try { await apiFetch(`/consignments/${id}/dispatch`, { method: 'POST', body: bondedAs ? { bonded_as: bondedAs } : {} }); refetch(); } catch (e) { setErr(e.message); }
  }
  async function removePallet(p) {
    setErr('');
    try { await apiFetch(`/consignments/${id}/pallets/${p.id}`, { method: 'DELETE' }); refetch(); } catch (e) { setErr(e.message); }
  }
  if (loading || !c) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  const open = c.status === 'open';
  const gross = c.pallets.reduce((t, p) => t + (Number(p.gross_weight_kg) || 0), 0);
  return (
    <div>
      {modal === 'edit' && <ConsignmentForm consignment={c} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {modal === 'bond' && (
        <Modal title="How is the in-bond spirit leaving?" onClose={() => setModal(null)} width={480}>
          <p style={{ margin: '0 0 12px', fontSize: 13.5 }}>{c.consignment_ref} has in-bond spirit on it and no order behind it, so Blake needs to know how to record it leaving.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['duty_paid', 'Duty paid on leaving', 'It leaves bond here — goes on the duty return'],
              ['under_bond', 'Under bond to another warehouse', 'Moved duty suspended (e.g. to the client\'s bond) — no duty'],
              ['export', 'Export', 'Leaving the UK duty suspended — no duty']].map(([k, label, hint]) => (
              <button key={k} type="button" onClick={() => dispatch(k)}
                style={{ textAlign: 'left', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text)' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{hint}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}
      {modal === 'add' && <AddPalletsModal consignment={c} onClose={() => setModal(null)} onDone={() => { setModal(null); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{c.consignment_ref}</h2>
            {c.title && <span style={{ fontSize: 17, fontWeight: 600 }}>{c.title}</span>}
            <StatusPill status={c.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {c.order_label || 'No order linked'}{c.client_name ? ` · ${c.client_name}` : ''}{c.dispatched_at ? ` · dispatched ${fmt(c.dispatched_at)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Btn size="sm" variant="ghost" onClick={() => setModal('edit')}>Edit details</Btn>
          <Btn size="sm" variant="ghost" onClick={() => openPdf(`/consignments/${id}/manifest.pdf`)}>Preview manifest</Btn>
          <PrintButton docType="consignment_manifest" path={`/consignments/${id}/manifest.pdf`} title={`Manifest ${c.consignment_ref}`} label="Print manifest" />
          {c.pallets.length > 0 && <Btn size="sm" variant="ghost" onClick={() => openPdf(`/storage-units/labels.pdf?consignment_id=${id}`)}>Preview all labels</Btn>}
          {c.pallets.length > 0 && <PrintButton docType="pallet_labels" path={`/storage-units/labels.pdf?consignment_id=${id}`} title={`Pallet labels ${c.consignment_ref}`} label={`Print ${c.pallets.length} label${c.pallets.length === 1 ? '' : 's'}`} />}
          {open && <Btn size="sm" onClick={() => dispatch()} disabled={!c.pallets.length}>Mark dispatched</Btn>}
          {c.status === 'dispatched' && <Btn size="sm" variant="ghost" onClick={() => act('reopen', 'Reopen it? Use this if it was marked dispatched by mistake. Stock it took off its pallets goes back on; an order it despatched stays despatched.')}>Reopen</Btn>}
          {open && <Btn size="sm" variant="danger" onClick={() => act('cancel', `Cancel ${c.consignment_ref}? Its pallets are freed up.`)}>Cancel</Btn>}
        </div>
      </div>
      <Err msg={err} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          ['Going to', [c.consignee_name, c.consignee_address].filter(Boolean).join(', ') || '—'],
          ['Contact', c.consignee_contact || '—'],
          ['Haulier', [c.haulier, c.haulier_ref].filter(Boolean).join(' · ') || '—'],
          ['Collection', fmt(c.collection_date)],
          ['Pallets', `${c.pallets.length}${gross ? ` · ${gross.toLocaleString('en-GB')} kg gross` : ''}`],
        ].map(([k, v]) => (
          <div key={k} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{v}</div>
          </div>
        ))}
      </div>
      {c.notes && <p style={{ fontSize: 13, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>{c.notes}</p>}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, flex: 1 }}>Pallets</h3>
        {open && <Btn size="sm" onClick={() => setModal('add')}>+ Add pallets</Btn>}
      </div>
      {!c.pallets.length ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No pallets yet. Add them here, or tick pallets on the Units tab and use "Add to consignment".</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{['Pallet', 'Name', 'Location', 'Contents', 'Gross kg', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {c.pallets.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, cursor: onOpenPallet ? 'pointer' : undefined, textDecoration: onOpenPallet ? 'underline' : undefined }} onClick={() => onOpenPallet?.(p.id)}>{p.unit_ref}</td>
                  <td style={{ padding: '8px 12px' }}>{p.name || '—'}{!p.is_active && <span style={{ marginLeft: 6, fontSize: 11, color: '#991b1b' }}>deleted</span>}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{p.location_code || '—'}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{p.contents.map(x => `${x.name} × ${Number(x.quantity)}`).join(', ') || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{p.gross_weight_kg ?? '—'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{open && <Btn size="sm" variant="ghost" onClick={() => removePallet(p)}>Remove</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ConsignmentList({ onOpen }) {
  const [filter, setFilter] = useState('open');
  const { data, loading, refetch } = useApi(`/consignments${filter === 'all' ? '' : `?status=${filter}`}`, [filter]);
  const rows = arr(data);
  const [creating, setCreating] = useState(false);
  return (
    <div>
      {creating && <ConsignmentForm onClose={() => setCreating(false)} onSaved={d => { setCreating(false); refetch(); if (d?.id) onOpen(d.id); }} />}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        {[['open', 'Open'], ['dispatched', 'Dispatched'], ['all', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === k ? 'var(--pld-green)' : 'var(--surface-2)', color: filter === k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
        ))}
        <span style={{ flex: 1 }} />
        <Btn size="sm" onClick={() => setCreating(true)}>+ New consignment</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : !rows.length ? (
        <div style={{ padding: '36px 20px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', fontSize: 13 }}>
          No consignments{filter !== 'all' ? ` ${filter}` : ''}. Group pallets going out together — tick them on the Units tab and use "Add to consignment".
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{['Ref', 'For', 'Going to', 'Pallets', 'Collection', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} onClick={() => onOpen(c.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700 }}>{c.consignment_ref}</td>
                  <td style={{ padding: '10px 12px' }}>{c.order_label || c.title || '—'}{c.client_name ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.client_name}</div> : null}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{c.consignee_name || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>{c.pallet_count}<div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{c.pallet_refs.slice(0, 4).join(', ')}{c.pallet_refs.length > 4 ? '…' : ''}</div></td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{fmt(c.collection_date)}</td>
                  <td style={{ padding: '10px 12px' }}><StatusPill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** On an order: the consignments (and pallets) going out for it. */
export function OrderConsignments({ kind, orderId }) {
  const param = kind === 'fulfilment_order' ? 'fulfilment_order_id' : 'sales_order_id';
  const { data, refetch } = useApi(`/consignments?${param}=${orderId}`, [orderId]);
  const rows = arr(data);
  const [creating, setCreating] = useState(false);
  return (
    <div style={{ marginTop: 24 }}>
      {creating && <ConsignmentForm presetOrder={{ kind, id: orderId }} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, flex: 1 }}>Pallets for this order</h3>
        <Btn size="sm" variant="ghost" onClick={() => setCreating(true)}>+ New consignment</Btn>
      </div>
      {!rows.length ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No pallets consigned to this order yet. Create a consignment, then add its pallets on Storage Units → Consignments.</p> : rows.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '9px 12px', marginBottom: 6, fontSize: 13, flexWrap: 'wrap' }}>
          <strong style={{ fontFamily: 'monospace' }}>{c.consignment_ref}</strong>
          <StatusPill status={c.status} />
          <span style={{ flex: 1 }}>{c.pallet_count} pallet{c.pallet_count === 1 ? '' : 's'}: <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{c.pallet_refs.join(', ') || '—'}</span></span>
          <Btn size="sm" variant="ghost" onClick={() => openPdf(`/consignments/${c.id}/manifest.pdf`)}>Manifest</Btn>
        </div>
      ))}
    </div>
  );
}
